import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { useAuthUser } from "react-auth-kit";
import { useLocation } from "react-router-dom";
import {
  TAB_NAV_ENABLED,
  HOME_TAB_PATH,
  buildTabKey,
  buildTabLocation,
  buildInitialTabState,
  createHomeTab,
  createTabId,
  getDefaultLabelForPath,
  isAuthOnlyPath,
  isStaticAssetPath,
  normalizePathname,
  normalizeSearch,
  persistTabState,
  flushPersistTabState,
} from "../utils/tabNavigation";

const K_CONFIRM_DIRTY_EXIT =
  "Are you sure to close the tab? There are unsaved changes.";

const TabActionsContext = createContext(null);
const TabStateContext = createContext(null);
const TabStoreContext = createContext(null);

const listRefreshSubscribersByPath = new Map();

function getListRefreshSubscribers(pathname) {
  const path = normalizePathname(pathname);
  if (!listRefreshSubscribersByPath.has(path)) {
    listRefreshSubscribersByPath.set(path, new Set());
  }
  return listRefreshSubscribersByPath.get(path);
}

export function subscribeListRefresh(pathname, callback) {
  const subscribers = getListRefreshSubscribers(pathname);
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
    const path = normalizePathname(pathname);
    if (!subscribers.size) {
      listRefreshSubscribersByPath.delete(path);
    }
  };
}

export function notifyListRefresh(pathname) {
  const path = normalizePathname(pathname);
  const subscribers = listRefreshSubscribersByPath.get(path);
  if (!subscribers?.size) return;
  subscribers.forEach((callback) => {
    try {
      callback();
    } catch (error) {
      console.error(error);
    }
  });
}

function createTab({ pathname, search = "", label, pinned = false }) {
  const normalizedPath = normalizePathname(pathname);
  const normalizedSearch = normalizeSearch(search);
  return {
    id: createTabId(),
    key: buildTabKey(normalizedPath, normalizedSearch),
    label: label || getDefaultLabelForPath(normalizedPath),
    pathname: normalizedPath,
    search: normalizedSearch,
    closable: !pinned,
    pinned,
  };
}

function createTabStore(initialState) {
  let state = {
    tabs: initialState?.tabs ?? [],
    activeTabId: initialState?.activeTabId ?? null,
    tabRefreshKeys: {},
  };
  const listeners = new Set();

  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    patchState: (updater) => {
      state = updater(state);
      listeners.forEach((listener) => listener());
    },
  };
}

function useTabStore() {
  const store = useContext(TabStoreContext);
  if (!store) {
    throw new Error("useTabStore must be used within TabProvider");
  }
  return store;
}

function buildActions(store, refs, userId) {
  const { dirtyCheckRef, tabHistoryRef, syncingUrlRef } = refs;

  const syncBrowserUrl = (tab) => {
    if (!tab || isAuthOnlyPath(tab.pathname)) return;
    const target = buildTabLocation(tab.pathname, tab.search);
    const current = buildTabLocation(
      window.location.pathname,
      window.location.search
    );
    if (target === current) return;

    syncingUrlRef.current = target;
    window.history.replaceState(null, "", target);
  };

  const activateTab = (
    tabId,
    { syncUrl = true, pushHistory = true, tabOverride = null } = {}
  ) => {
    const { tabs, activeTabId } = store.getState();
    if (activeTabId === tabId && !tabOverride) {
      if (syncUrl) {
        const tab = tabs.find((t) => t.id === tabId);
        syncBrowserUrl(tab);
      }
      return;
    }

    store.patchState((prev) => ({ ...prev, activeTabId: tabId }));

    if (pushHistory) {
      tabHistoryRef.current = [
        ...tabHistoryRef.current.filter((id) => id !== tabId),
        tabId,
      ];
    }
    if (syncUrl) {
      const tab =
        tabOverride ?? store.getState().tabs.find((t) => t.id === tabId);
      syncBrowserUrl(tab);
    }
  };

  const openTab = ({ pathname, search = "", label, pinned = false }) => {
    const normalizedPath = normalizePathname(pathname);
    const normalizedSearch = normalizeSearch(search);
    const key = buildTabKey(normalizedPath, normalizedSearch);

    let targetTabId = null;
    let targetTab = null;

    store.patchState((prev) => {
      const existing = prev.tabs.find((tab) => tab.key === key);
      if (existing) {
        targetTabId = existing.id;
        targetTab = existing;
        return prev;
      }

      const nextTab = createTab({
        pathname: normalizedPath,
        search: normalizedSearch,
        label,
        pinned,
      });
      targetTabId = nextTab.id;
      targetTab = nextTab;
      return {
        ...prev,
        tabs: [...prev.tabs, nextTab],
      };
    });

    if (targetTabId) {
      activateTab(targetTabId, { tabOverride: targetTab });
    }

    return { id: targetTabId, key };
  };

  const replaceActiveTab = ({ pathname, search = "", label }) => {
    const normalizedPath = normalizePathname(pathname);
    const normalizedSearch = normalizeSearch(search);
    const key = buildTabKey(normalizedPath, normalizedSearch);
    const { activeTabId, tabs } = store.getState();

    if (!activeTabId) {
      return openTab({ pathname, search, label });
    }

    const existing = tabs.find((tab) => tab.key === key);
    if (existing && existing.id !== activeTabId) {
      const activeTab = tabs.find((tab) => tab.id === activeTabId);
      if (activeTab?.closable) {
        closeTabs([activeTabId], {
          survivorTabId: existing.id,
          skipDirtyCheck: true,
        });
      } else {
        activateTab(existing.id);
      }
      return { id: existing.id, key };
    }

    let targetTab = null;
    store.patchState((prev) => ({
      ...prev,
      tabs: prev.tabs.map((tab) => {
        if (tab.id !== activeTabId) return tab;
        targetTab = {
          ...tab,
          key,
          pathname: normalizedPath,
          search: normalizedSearch,
          label: label || tab.label,
        };
        return targetTab;
      }),
      tabRefreshKeys: {
        ...prev.tabRefreshKeys,
        [activeTabId]: (prev.tabRefreshKeys[activeTabId] || 0) + 1,
      },
    }));

    if (targetTab) {
      syncBrowserUrl(targetTab);
    }

    return { id: activeTabId, key };
  };

  const closeTabs = (
    tabIds,
    { survivorTabId = null, skipDirtyCheck = false } = {}
  ) => {
    const { tabs, activeTabId } = store.getState();
    const toClose = tabIds.filter((id) => {
      const tab = tabs.find((t) => t.id === id);
      return tab?.closable;
    });
    if (!toClose.length) return false;

    if (!skipDirtyCheck) {
      const anyDirty = toClose.some((id) => dirtyCheckRef.current[id]?.());
      if (anyDirty && !window.confirm(K_CONFIRM_DIRTY_EXIT)) return false;
    }

    const toCloseSet = new Set(toClose);
    toClose.forEach((id) => delete dirtyCheckRef.current[id]);
    tabHistoryRef.current = tabHistoryRef.current.filter(
      (id) => !toCloseSet.has(id)
    );

    const index = tabs.findIndex((t) => t.id === activeTabId);
    const nextTabs = tabs.filter((t) => !toCloseSet.has(t.id));

    store.patchState((prev) => {
      const nextRefreshKeys = { ...prev.tabRefreshKeys };
      toClose.forEach((id) => delete nextRefreshKeys[id]);
      return {
        ...prev,
        tabs: nextTabs,
        tabRefreshKeys: nextRefreshKeys,
      };
    });

    persistTabState(userId, nextTabs, activeTabId, { immediate: true });

    const survivor =
      survivorTabId && nextTabs.some((t) => t.id === survivorTabId)
        ? survivorTabId
        : null;

    if (survivor) {
      activateTab(survivor);
    } else if (toCloseSet.has(activeTabId)) {
      const fallback =
        nextTabs[Math.min(index, nextTabs.length - 1)] ?? nextTabs[0];
      if (fallback) activateTab(fallback.id);
    }

    return true;
  };

  return {
    enabled: TAB_NAV_ENABLED,
    openTab,
    replaceActiveTab,
    closeTab: (tabId, options) => closeTabs([tabId], options),
    closeTabsToRight: (tabId) => {
      const { tabs } = store.getState();
      const index = tabs.findIndex((t) => t.id === tabId);
      if (index < 0) return;
      const toClose = tabs
        .slice(index + 1)
        .filter((tab) => tab.closable)
        .map((tab) => tab.id);
      closeTabs(toClose);
    },
    closeOtherTabs: (tabId) => {
      const { tabs } = store.getState();
      const toClose = tabs
        .filter((tab) => tab.id !== tabId && tab.closable)
        .map((tab) => tab.id);
      closeTabs(toClose, { survivorTabId: tabId });
    },
    refreshTab: (tabId) => {
      if (!store.getState().tabs.some((tab) => tab.id === tabId)) return;
      store.patchState((prev) => ({
        ...prev,
        tabRefreshKeys: {
          ...prev.tabRefreshKeys,
          [tabId]: (prev.tabRefreshKeys[tabId] || 0) + 1,
        },
      }));
    },
    subscribeListRefresh,
    notifyListRefresh,
    activateTab,
    updateTabLabel: (tabId, label) => {
      if (!label) return;
      store.patchState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) => {
          if (tab.id !== tabId) return tab;
          if (tab.pinned || tab.id === "home-tab") return tab;
          return { ...tab, label };
        }),
      }));
    },
    updateTabLabelByKey: (key, label) => {
      if (!label || !key) return;
      store.patchState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) => {
          if (tab.key !== key) return tab;
          if (tab.pinned || tab.id === "home-tab") return tab;
          return { ...tab, label };
        }),
      }));
    },
    updateTabLocation: (tabId, pathname, search) => {
      const normalizedPath = normalizePathname(pathname);
      const normalizedSearch = normalizeSearch(search);
      if (isAuthOnlyPath(normalizedPath)) return;

      store.patchState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) => {
          if (tab.id !== tabId) return tab;

          const nextPath = buildTabLocation(normalizedPath, normalizedSearch);
          const currentPath = buildTabLocation(tab.pathname, tab.search);
          if (nextPath === currentPath) return tab;

          return {
            ...tab,
            pathname: normalizedPath,
            search: normalizedSearch,
          };
        }),
      }));
    },
    registerDirtyCheck: (tabId, fn) => {
      if (!tabId) return;
      if (fn) {
        dirtyCheckRef.current[tabId] = fn;
        return;
      }
      delete dirtyCheckRef.current[tabId];
    },
    getActiveTabId: () => store.getState().activeTabId,
    getTabs: () => store.getState().tabs,
  };
}

function TabControllerEffects({
  store,
  syncingUrlRef,
  dirtyCheckRef,
  restoredFromPersistenceRef,
  userId,
}) {
  const location = useLocation();
  const state = useSyncExternalStore(
    store.subscribe,
    store.getState,
    store.getState
  );

  useEffect(() => {
    if (!TAB_NAV_ENABLED || !userId || !state.tabs.length) return;
    persistTabState(userId, state.tabs, state.activeTabId);
  }, [userId, state.tabs, state.activeTabId]);

  useEffect(() => {
    const onBeforeUnload = () => flushPersistTabState();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  useEffect(() => {
    if (!TAB_NAV_ENABLED) return;
    const tabIds = new Set(state.tabs.map((tab) => tab.id));
    Object.keys(dirtyCheckRef.current).forEach((tabId) => {
      if (!tabIds.has(tabId)) delete dirtyCheckRef.current[tabId];
    });
  }, [state.tabs, dirtyCheckRef]);

  useEffect(() => {
    if (!TAB_NAV_ENABLED) return;

    const current = buildTabLocation(location.pathname, location.search);
    const pendingSync = syncingUrlRef.current;

    if (pendingSync) {
      if (pendingSync === current) {
        syncingUrlRef.current = null;
        if (restoredFromPersistenceRef.current) {
          restoredFromPersistenceRef.current = false;
        }
      } else {
        return;
      }
    }

    if (restoredFromPersistenceRef.current) {
      const { tabs, activeTabId } = store.getState();
      const activeTab =
        tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
      if (activeTab) {
        const target = buildTabLocation(activeTab.pathname, activeTab.search);
        if (current === target) {
          restoredFromPersistenceRef.current = false;
        }
      }
      return;
    }

    const key = buildTabKey(location.pathname, location.search);
    const existing = store.getState().tabs.find((tab) => tab.key === key);
    if (existing) {
      const { activeTabId } = store.getState();
      if (activeTabId !== existing.id) {
        store.patchState((prev) => ({ ...prev, activeTabId: existing.id }));
      }
      return;
    }

    if (location.pathname === "/login") return;

    const normalizedPath = normalizePathname(location.pathname);
    if (
      (normalizedPath === "/" || normalizedPath === HOME_TAB_PATH) &&
      !location.search
    ) {
      const homeId = store.getState().tabs[0]?.id ?? "home-tab";
      const { activeTabId } = store.getState();
      if (activeTabId !== homeId) {
        store.patchState((prev) => ({ ...prev, activeTabId: homeId }));
      }
    }
  }, [
    location.pathname,
    location.search,
    store,
    syncingUrlRef,
    restoredFromPersistenceRef,
  ]);

  return null;
}

export function TabProvider({ children }) {
  const auth = useAuthUser();
  const user = auth();
  const userId = user?.id || user?._id;

  const dirtyCheckRef = useRef({});
  const tabHistoryRef = useRef([]);
  const syncingUrlRef = useRef(null);
  const restoredFromPersistenceRef = useRef(false);

  const storeRef = useRef(null);
  if (storeRef.current == null && userId) {
    const initialState = TAB_NAV_ENABLED
      ? buildInitialTabState(userId, window.location)
      : null;

    if (initialState?.restoredFromPersistence) {
      restoredFromPersistenceRef.current = true;
      const activeTab =
        initialState.tabs.find((tab) => tab.id === initialState.activeTabId) ??
        initialState.tabs[0];
      if (activeTab && !isStaticAssetPath(window.location.pathname)) {
        const target = buildTabLocation(activeTab.pathname, activeTab.search);
        syncingUrlRef.current = target;
        window.history.replaceState(null, "", target);
      }
    }

    const { restoredFromPersistence: _restored, ...storeState } =
      initialState ?? {};
    storeRef.current = createTabStore(storeState);
  }
  const store = storeRef.current;

  const actionsRef = useRef(null);
  if (actionsRef.current == null && store) {
    actionsRef.current = buildActions(
      store,
      {
        dirtyCheckRef,
        tabHistoryRef,
        syncingUrlRef,
      },
      userId
    );
  }
  const actions = actionsRef.current;

  if (!TAB_NAV_ENABLED) {
    return children;
  }

  if (!store || !actions) {
    return null;
  }

  return (
    <TabStoreContext.Provider value={store}>
      <TabActionsContext.Provider value={actions}>
        <TabControllerEffects
          store={store}
          syncingUrlRef={syncingUrlRef}
          dirtyCheckRef={dirtyCheckRef}
          restoredFromPersistenceRef={restoredFromPersistenceRef}
          userId={userId}
        />
        {children}
      </TabActionsContext.Provider>
    </TabStoreContext.Provider>
  );
}

export function TabStateOutlet({ children }) {
  const store = useTabStore();
  const state = useSyncExternalStore(
    store.subscribe,
    store.getState,
    store.getState
  );

  const activeTab = useMemo(
    () => state.tabs.find((tab) => tab.id === state.activeTabId) ?? state.tabs[0],
    [state.tabs, state.activeTabId]
  );

  const stateValue = useMemo(
    () => ({
      tabs: state.tabs,
      activeTabId: state.activeTabId,
      activeTab,
      tabRefreshKeys: state.tabRefreshKeys,
    }),
    [state.tabs, state.activeTabId, activeTab, state.tabRefreshKeys]
  );

  if (!TAB_NAV_ENABLED) {
    return children;
  }

  return (
    <TabStateContext.Provider value={stateValue}>
      {children}
    </TabStateContext.Provider>
  );
}

export function useTabActions() {
  const context = useContext(TabActionsContext);
  if (!context) {
    throw new Error("useTabActions must be used within TabProvider");
  }
  return context;
}

export function useTabActionsOptional() {
  return useContext(TabActionsContext);
}

export function useTabState() {
  const context = useContext(TabStateContext);
  if (!context) {
    throw new Error("useTabState must be used within TabStateOutlet");
  }
  return context;
}

export function useTabStateOptional() {
  return useContext(TabStateContext);
}

export function useTabContext() {
  const actions = useTabActions();
  const state = useTabState();
  return { ...actions, ...state };
}

export function useTabContextOptional() {
  const actions = useTabActionsOptional();
  const state = useTabStateOptional();
  if (!actions) return null;
  return { ...actions, ...(state ?? {}) };
}
